import { MathBase } from "./../../math/MathBase";
import { SecurityErrorLOFactory } from "./../../externalLogging/factories/SecurityErrorLOFactory";
import { IOErrorLOFactory } from "./../../externalLogging/factories/IOErrorLOFactory";
import { ExternalLog } from "./../../externalLogging/ExternalLog";


import { EnvGlobalsHandler } from "../EnvGlobalsHandler";
import { IEnvironmentGlobals } from "../IEnvironmentGlobals";
import { PathProvider } from "../environment/providers/PathProvider";
import { BasicSoundEvent } from "../event/BasicSoundEvent";
import { URLRequest } from "@goodgamestudios/createjs-utilities";
import { Sound, SoundChannel, SoundTransform } from "@goodgamestudios/createjs-utilities";

import Event = createjs.Event;
import EventDispatcher = createjs.EventDispatcher;
import IOErrorEvent = createjs.IOErrorEvent;
import ProgressEvent = createjs.ProgressEvent;
import SecurityErrorEvent = createjs.SecurityErrorEvent;

export class BasicSoundController extends EventDispatcher {
    private static BUFFER_SONG_LEGTH: number = 0.4;

    private _globalSoundTransform: SoundTransform = new SoundTransform();
    protected _effectsTransform: SoundTransform = new SoundTransform();
    private _musicTransform: SoundTransform = new SoundTransform();

    private _isMuted: boolean = false;
    private _isEffectsMuted: boolean = false;
    private _isMusicMuted: boolean = true;

    private _globalSavedVolume: number = 1;
    private _musicSavedVolume: number = 1;
    private _effectsSavedVolume: number = 1;

    private _currentSongChannel: SoundChannel;
    private _currentSong: Sound;
    private _currentSongString: string;
    private _currentSongLoops: number;
    public loadedSongs: Map<string, Sound>;

    private _playingSoundEffects: SoundChannel[];

    private _defaultPlayInstanceConfig: createjs.PlayPropsConfig;

    constructor() {
        super();
    }

    public initialize(): void {
        this.loadedSongs = new Map<string, Sound>();
        this._playingSoundEffects = [];
    }

    public playSoundEffectByName(nameString: string, loops: number = 0): SoundChannel {
        if (this.isEffectsMuted) {
            return null;
        }
        let soundEffect: Sound;
        if (soundEffect = this.loadedSongs.get(nameString)) {
            return soundEffect.play(0, loops);
        } else {
            if (createjs.Sound.loadComplete(nameString)) {
                // preloaded by audiosprites
                let soundInAudioSprites = new Sound(new URLRequest(nameString));
                this.loadedSongs.set(nameString, soundInAudioSprites);
                return soundInAudioSprites.play(0, loops);
            } else {
                this.loadedSongs.set(nameString, this.loadMusicFile(PathProvider.instance.getSoundURL(nameString)));
            }
        }
        return null;
    }

    public playSoundEffect(sfx: Sound, loops: number = 0): SoundChannel {
        if (this.isEffectsMuted) {
            return null;
        }
        let soundChannel: SoundChannel = sfx.play(0, loops, this._effectsTransform);
        return soundChannel;
    }

    public playLoopedSoundEffect(sfx: Sound, loops: number = 0): SoundChannel {
        if (this.isEffectsMuted) {
            return null;
        }
        let soundChannel: SoundChannel = sfx.play(0, -1, this._effectsTransform);
        this._playingSoundEffects.push(soundChannel);
        return soundChannel;
    }

    public stopAllLoopedSoundEffects(): void {
        for (let i: number = 0; i < this._playingSoundEffects.length; i++) {
            let sfx: SoundChannel = this._playingSoundEffects[i];
            sfx.stop();
            sfx = null;
        }
        this._playingSoundEffects = [];
    }

    public stopLoopedSoundEffect(loopedSfx: SoundChannel): void {
        for (let i: number = 0; i < this._playingSoundEffects.length; i++) {
            let sfx: SoundChannel = this._playingSoundEffects[i];

            if (sfx == loopedSfx) {
                sfx.stop();
                this._playingSoundEffects.splice(i, 1);
                return;
            }
        }
    }

    public playMusic(nameString: string, loops: number = 0, restart: boolean = false): void {
        if (this._currentSongString === nameString && !restart) {
            return;
        }
        this._currentSongString = nameString;
        this._currentSongLoops = loops;


        if( !this._isMusicMuted ) {
            if (this.loadedSongs.get(nameString)) {
                this.dispatchEvent(new BasicSoundEvent(BasicSoundEvent.SONG_BUFFERED_COMPLETE));
                let song: Sound = this.loadedSongs.get(nameString);

                if (song.bytesLoaded === song.bytesTotal) {
                    this.playSong(song, loops);
                }

            } else {
                this.loadedSongs.set(nameString, this.loadMusicFile(PathProvider.instance.getSoundURL(nameString)));
            }
        }
    }

    public stopMusic(): void {
        if (this._currentSongChannel != null) {
            this._currentSongChannel.stop();
            this._currentSongChannel.removeEventListener(Event.SOUND_COMPLETE, this.bindFunction(this.onCurrentSongReachedEnd));
        }
        this._currentSongString = "";
    }

    public stopAllSounds(): void {
        this.stopAllLoopedSoundEffects();
        this.stopMusic();
    }

    public preLoadMusic(nameString: string): void {
        if (!this.loadedSongs.get(nameString)) {
            let urlRequest: URLRequest = new URLRequest(PathProvider.instance.getSoundURL(nameString));
            let snd: Sound = new Sound(urlRequest);
            snd.addEventListener(IOErrorEvent.IO_ERROR, this.bindFunction(this.onStreamError));
        }
    }

    protected onStreamError(e: IOErrorEvent): void {
        throw new Error("Fehler beim Streamen der Sounddatei! \n\n" + (((<Sound>e.target)).url) + "\n\n" + e.text);
    }

    protected playSong(song: Sound, loops: number = 0): void {
        if (song == null) {
            return;
        }

        if (this._currentSongChannel != null) {
            this._currentSongChannel.stop();
        }
        this._currentSongChannel = song.play(0, loops /*int.MAX_VALUE*/, this._musicTransform);
        if (this._currentSongChannel != null) {
            this._currentSongChannel.addEventListener(Event.SOUND_COMPLETE, this.bindFunction(this.onCurrentSongReachedEnd));
            this._currentSong = song;
        }
    }

    private onCurrentSongReachedEnd(e: Event): void {
        this.dispatchEvent(new BasicSoundEvent(BasicSoundEvent.SONG_PLAYED_COMPLETE));
    }

    private loadMusicFile(url: string): Sound {
        let sound: Sound = new Sound();
        sound.addEventListener(Event.COMPLETE, this.bindFunction(this.onMusicFileLoadComplete));
        sound.addEventListener(ProgressEvent.PROGRESS, this.bindFunction(this.onMusicFileLoadProgress));
        sound.addEventListener(IOErrorEvent.IO_ERROR, this.bindFunction(this.onMusicFileLoadIOError));
        sound.addEventListener(SecurityErrorEvent.SECURITY_ERROR, this.bindFunction(this.onMusicFileLoadSecurityError));
        sound.load(new URLRequest(url));

        return sound;
    }

    private onMusicFileLoadIOError(ioe: IOErrorEvent): void {
        ExternalLog.logger.log(new IOErrorLOFactory(IOErrorLOFactory.GENERAL_LOADER_IO_ERROR, ioe.text, ioe.target.url));
        ioe.target.removeEventListener(Event.COMPLETE, this.bindFunction(this.onMusicFileLoadComplete));
        ioe.target.removeEventListener(ProgressEvent.PROGRESS, this.bindFunction(this.onMusicFileLoadProgress));
        ioe.target.removeEventListener(IOErrorEvent.IO_ERROR, this.bindFunction(this.onMusicFileLoadIOError));
        ioe.target.removeEventListener(SecurityErrorEvent.SECURITY_ERROR, this.bindFunction(this.onMusicFileLoadSecurityError));
    }

    private onMusicFileLoadSecurityError(se: SecurityErrorEvent): void {
        ExternalLog.logger.log(new SecurityErrorLOFactory(SecurityErrorLOFactory.GENERAL_LOADER_SECURITY_ERROR, se.text, se.target.url));
        se.target.removeEventListener(Event.COMPLETE, this.bindFunction(this.onMusicFileLoadComplete));
        se.target.removeEventListener(ProgressEvent.PROGRESS, this.bindFunction(this.onMusicFileLoadProgress));
        se.target.removeEventListener(IOErrorEvent.IO_ERROR, this.bindFunction(this.onMusicFileLoadIOError));
        se.target.removeEventListener(SecurityErrorEvent.SECURITY_ERROR, this.bindFunction(this.onMusicFileLoadSecurityError));
    }

    private onMusicFileLoadComplete(event: Event): void {
        event.target.removeEventListener(Event.COMPLETE, this.bindFunction(this.onMusicFileLoadComplete));
        event.target.removeEventListener(ProgressEvent.PROGRESS, this.bindFunction(this.onMusicFileLoadProgress));
        event.target.removeEventListener(IOErrorEvent.IO_ERROR, this.bindFunction(this.onMusicFileLoadIOError));
        event.target.removeEventListener(SecurityErrorEvent.SECURITY_ERROR, this.bindFunction(this.onMusicFileLoadSecurityError));
        this.dispatchEvent(new BasicSoundEvent(BasicSoundEvent.SONG_BUFFERED_COMPLETE));
        let sound: Sound = event.target;
        this.playSong(sound, this._currentSongLoops);
    }

    //@HTML5 this one is not used, move play logic to onMusicFileLoadComplete
    private onMusicFileLoadProgress(event: Event): void {
        let sound: Sound = (<Sound>event.target);

        if (sound.bytesLoaded > sound.bytesTotal * BasicSoundController.BUFFER_SONG_LEGTH) {
            sound.removeEventListener(IOErrorEvent.IO_ERROR, this.bindFunction(this.onMusicFileLoadIOError));
            sound.removeEventListener(SecurityErrorEvent.SECURITY_ERROR, this.bindFunction(this.onMusicFileLoadSecurityError));
            sound.removeEventListener(ProgressEvent.PROGRESS, this.bindFunction(this.onMusicFileLoadProgress));
            this.dispatchEvent(new BasicSoundEvent(BasicSoundEvent.SONG_BUFFERED_ENOUGH));
            this.playSong((<Sound>event.target), this._currentSongLoops);
        }
    }

    public toggleMute(): void {
        if (!this._isMuted) {
            this.muteAll();
        } else {
            this.unMuteAll();
        }
    }

    public toggleMuteMusic(): void {
        if (!this._isMusicMuted) {
            this.muteMusic();
        } else {
            this.unMuteMusic();
        }
    }

    public toggleMuteEffects(): void {
        if (!this._isEffectsMuted) {
            this.muteEffects();
        } else {
            this.unmuteEffects();
        }
    }

    public muteAll(): void {
        createjs.Sound.muted  = this._isMuted = true;
    }

    public unMuteAll(): void {
        createjs.Sound.muted = this._isMuted =  false;
    }

    public muteMusic(): void {
        this._isMusicMuted = true;
        this._musicSavedVolume = this._musicTransform.volume;
        this._musicTransform.volume = 0;
        this.applyMusicSoundTransform();
    }

    public unMuteMusic(): void {
        this._isMusicMuted = false;
        this._musicTransform.volume = this._musicSavedVolume;
        this.applyMusicSoundTransform();

        if( !this._currentSong && this._currentSongString ) {
            this.playMusic( this.currentSongString, this._currentSongLoops, true );
        }
    }

    public muteEffects(): void {
        this._isEffectsMuted = true;
        this._effectsSavedVolume = this._effectsTransform.volume;
        this._effectsTransform.volume = 0;
    }

    public unmuteEffects(): void {
        this._isEffectsMuted = false;
        this._effectsTransform.volume = this._effectsSavedVolume;
    }

    public get musicVolume(): number {
        return this._musicTransform.volume;
    }

    public get isEffectsMuted(): boolean {
        return this._isMuted || this._isEffectsMuted;
    }

    public get isMusicMuted(): boolean {
        return this._isMuted || this._isMusicMuted;
    }

    public get isMuted(): boolean {
        return this._isMuted;
    }

    public setMusicVolume(volume: number): void {
        if (!this._isMusicMuted) {
            this._musicTransform.volume = MathBase.clamp(volume, 0, 1);
            this.applyMusicSoundTransform();
        } else {
            this._musicSavedVolume = MathBase.clamp(volume, 0, 1);
        }
    }

    public setEffectsVolume(volume: number): void {
        this._effectsTransform.volume = MathBase.clamp(volume, 0, 1);
    }

    public get musicMaxPeak(): number {
        return Math.max(this._currentSongChannel.leftPeak, this._currentSongChannel.rightPeak);
    }

    private applyMusicSoundTransform(): void {
        if (this._currentSongChannel != null) {
            this._currentSongChannel.soundTransform = this._musicTransform;
        }
    }

    protected get env(): IEnvironmentGlobals {
        return EnvGlobalsHandler.globals;
    }

    public get currentSongString(): string {
        return this._currentSongString;
    }

    public get currentSongChannel(): SoundChannel {
         return this._currentSongChannel;
    }
}

